import { safeLocalStorage } from '@zealot/shared';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { type BrandScopeSlice, createBrandScopeSlice } from './slices/brandScope';
import { type BrandThemeSlice, createBrandThemeSlice, pickBrandTheme } from './slices/brandTheme';
import { type CurrentUserSlice, createCurrentUserSlice } from './slices/currentUser';
import { createHubBrandSlice, type HubBrandSlice } from './slices/hubBrand';
import {
  brandThemeFromLegacyKeys,
  HUB_BRAND_STORAGE_KEY,
  mergeHubBrand,
  pickHubIdentity,
} from './slices/hubBrandPersistence';
import { createNavigationSlice, type NavigationSlice } from './slices/navigation';

export type AppStore = BrandScopeSlice & BrandThemeSlice & CurrentUserSlice & HubBrandSlice & NavigationSlice;

// gloss
export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (...a) => ({
        ...createBrandScopeSlice(...a),
        ...createBrandThemeSlice(...a),
        ...createCurrentUserSlice(...a),
        ...createHubBrandSlice(...a),
        ...createNavigationSlice(...a),
      }),
      {
        name: HUB_BRAND_STORAGE_KEY,
        storage: createJSONStorage(() => safeLocalStorage),
        partialize: (state) => ({
          hubBrand: pickHubIdentity(state.hubBrand),
          brandTheme: pickBrandTheme(state.brandTheme),
        }),
        merge: (persisted, current) => {
          const stored = persisted as Partial<AppStore> | undefined;
          const hubBrand = mergeHubBrand(stored?.hubBrand);

          return {
            ...current,
            hubBrand: { ...current.hubBrand, ...hubBrand },
            brandTheme: hubBrand.uuid
              ? { ...current.brandTheme, ...pickBrandTheme(stored?.brandTheme ?? brandThemeFromLegacyKeys()) }
              : current.brandTheme,
          };
        },
      },
    ),
    { name: 'AdvocatePortalStore', enabled: process.env.NODE_ENV !== 'production' },
  ),
);

export const useNavigation = (): NavigationSlice['navigation'] => useAppStore((s) => s.navigation);

// gloss
export const useHubBrand = (): HubBrandSlice['hubBrand'] => useAppStore((s) => s.hubBrand);

// gloss
export const getHubBrand = (): HubBrandSlice['hubBrand'] => useAppStore.getState().hubBrand;

// gloss
export const getHubBrandName = (): string | null => getHubBrand().brandName ?? null;

// gloss
export const hubBrandUuidForRequest = (): string | undefined => {
  const hub = getHubBrand();

  return hub.isSwitching ? undefined : hub.uuid;
};

// gloss
export const hubStillAnswersTo = (brandID: string | null): boolean => {
  const hub = getHubBrand();

  return !hub.isSwitching && (hub.brandID ?? null) === brandID;
};

export const useStoredBrandTheme = (): BrandThemeSlice['brandTheme'] => useAppStore((s) => s.brandTheme);

export const getStoredBrandTheme = (): BrandThemeSlice['brandTheme'] => useAppStore.getState().brandTheme;
